import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShieldCheck, CheckCircle2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import usePageMeta from "../../lib/usePageMeta";

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:border-transparent";
const MAX_MB = 8;

const PREFERENCE_OPTIONS = [
  { value: "full_time", label: "Full-Time" },
  { value: "part_time", label: "Part-Time" },
  { value: "per_diem", label: "Per Diem" },
  { value: "contract", label: "Contract" },
  { value: "temporary", label: "Temporary" },
  { value: "internship", label: "Internship" },
  { value: "other", label: "Other" },
];

const initial = {
  first_name: "", last_name: "", email: "", phone: "", city: "", state: "",
  linkedin: "", website: "", current_employer: "", current_title: "",
  cover_letter: "", referral_source: "", additional_comments: "",
  employment_preferences_other: "",
};

export default function GeneralApply() {
  usePageMeta({
    title: "General Employment Application | California Corporate Chaplains",
    description: "Submit a general employment application to California Corporate Chaplains for future consideration.",
  });
  const [form, setForm] = useState(initial);
  const [prefs, setPrefs] = useState([]);
  const [resume, setResume] = useState(null);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const togglePref = (v) => setPrefs((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]));

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setResume(null); return; }
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      toast.error("Résumé must be a PDF, DOC, or DOCX file.");
      e.target.value = ""; setResume(null); return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Résumé exceeds the ${MAX_MB} MB limit.`);
      e.target.value = ""; setResume(null); return;
    }
    setResume(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Please confirm the consent statement before submitting.");
    if (!resume) return toast.error("Please attach your résumé (PDF, DOC, or DOCX).");
    if (prefs.length === 0) return toast.error("Please select at least one employment type you would consider.");
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== "") fd.append(k, v); });
      fd.append("employment_preferences", prefs.join(","));
      fd.append("consent", "true");
      fd.append("resume", resume);
      await api.post("/careers-general-apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="general-apply-success">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12 text-center shadow-sm">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-50 text-green-600 mb-6"><CheckCircle2 className="w-7 h-7" /></div>
          <h1 className="font-heading text-3xl tracking-tight text-slate-900">Thank you for your interest.</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Your general employment application has been received by California Corporate Chaplains and will be reviewed. Submission of an application does not guarantee an interview or employment.
          </p>
          <Link to="/careers" className="mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">Back to careers</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="general-apply-page">
      <Link to="/careers" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#4A6FA5] mb-6"><ArrowLeft className="w-4 h-4" /> Back to Careers</Link>
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">General Employment Application</p>
      <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">Don't see the right opportunity? Submit a general application.</h1>
      <p className="mt-3 text-slate-600 leading-relaxed">We'll keep your information on file and reach out if a matching opportunity opens. Submission of an application does not guarantee an interview or employment.</p>

      <div className="mt-6 rounded-xl border border-slate-200 bg-[#F8FAFC] p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" />
        <p className="text-sm text-slate-700 leading-relaxed">
          Please do not include Social Security numbers, dates of birth, driver's license numbers, medical information, financial information, or other sensitive personal information in your application or uploaded documents.
        </p>
      </div>

      <form onSubmit={submit} data-testid="general-apply-form" className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-5 shadow-sm">
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="First name" required><input required data-testid="gapply-first-name" value={form.first_name} onChange={update("first_name")} className={inputCls} /></Field>
          <Field label="Last name" required><input required data-testid="gapply-last-name" value={form.last_name} onChange={update("last_name")} className={inputCls} /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Email" required><input required type="email" data-testid="gapply-email" value={form.email} onChange={update("email")} className={inputCls} /></Field>
          <Field label="Phone" required><input required type="tel" data-testid="gapply-phone" value={form.phone} onChange={update("phone")} className={inputCls} /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="City" required><input required data-testid="gapply-city" value={form.city} onChange={update("city")} className={inputCls} /></Field>
          <Field label="State" required><input required data-testid="gapply-state" value={form.state} onChange={update("state")} className={inputCls} /></Field>
        </div>

        <fieldset data-testid="gapply-prefs" className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <legend className="px-1.5 text-sm font-medium text-slate-700">Employment types you would consider <span className="text-[#4A6FA5]">*</span></legend>
          <p className="text-xs text-slate-500 mb-3">Select all that apply.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {PREFERENCE_OPTIONS.map((o) => (
              <label key={o.value} className="inline-flex items-center gap-2 text-sm text-slate-800 rounded-lg border border-slate-200 bg-white px-3 py-2 cursor-pointer hover:border-[#4A6FA5]">
                <input type="checkbox" checked={prefs.includes(o.value)} onChange={() => togglePref(o.value)} data-testid={`gapply-pref-${o.value}`} className="h-4 w-4 rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]" />
                {o.label}
              </label>
            ))}
          </div>
          {prefs.includes("other") && (
            <div className="mt-3">
              <Field label="Other — please describe (optional)">
                <input data-testid="gapply-pref-other-text" value={form.employment_preferences_other} onChange={update("employment_preferences_other")} className={inputCls} placeholder="Brief description" />
              </Field>
            </div>
          )}
        </fieldset>

        <Field label={`Résumé (PDF, DOC, or DOCX — max ${MAX_MB} MB)`} required>
          <label className="flex items-center gap-3 cursor-pointer rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3 hover:border-[#4A6FA5]">
            <Upload className="w-5 h-5 text-[#4A6FA5]" />
            <span className="text-sm text-slate-700">{resume ? resume.name : "Choose file to upload"}</span>
            <input required type="file" accept=".pdf,.doc,.docx" onChange={onFileChange} data-testid="gapply-resume" className="sr-only" />
          </label>
        </Field>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="LinkedIn profile URL"><input type="url" data-testid="gapply-linkedin" value={form.linkedin} onChange={update("linkedin")} className={inputCls} /></Field>
          <Field label="Professional website"><input type="url" data-testid="gapply-website" value={form.website} onChange={update("website")} className={inputCls} /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Current employer"><input data-testid="gapply-current-employer" value={form.current_employer} onChange={update("current_employer")} className={inputCls} /></Field>
          <Field label="Current job title"><input data-testid="gapply-current-title" value={form.current_title} onChange={update("current_title")} className={inputCls} /></Field>
        </div>
        <Field label="Cover letter / statement of interest">
          <textarea rows={6} data-testid="gapply-cover-letter" value={form.cover_letter} onChange={update("cover_letter")} className={`${inputCls} resize-none`} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="How did you hear about us?"><input data-testid="gapply-referral" value={form.referral_source} onChange={update("referral_source")} className={inputCls} /></Field>
          <Field label="Additional comments"><input data-testid="gapply-additional" value={form.additional_comments} onChange={update("additional_comments")} className={inputCls} /></Field>
        </div>

        <label className="flex items-start gap-3 text-sm text-slate-700 pt-2">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="gapply-consent" className="mt-1 h-4 w-4 rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]" />
          <span>I am voluntarily submitting this information for employment consideration by California Corporate Chaplains. I understand that submission of this application does not guarantee an interview or employment.</span>
        </label>

        <button type="submit" disabled={submitting} data-testid="gapply-submit" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] disabled:opacity-60">
          {submitting ? "Submitting…" : "Submit General Application"} <ArrowRight className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (<label className="block"><span className="block text-sm font-medium text-slate-700 mb-1.5">{label} {required && <span className="text-[#4A6FA5]">*</span>}</span>{children}</label>);
}

export { PREFERENCE_OPTIONS };
